import React from "react";
import { Card, CardContent } from "@components/ui/card";

export type OptionsMlFeature = {
  feature?: string;
  label?: string;
  importance?: number | null;
  importance_pct?: number | null;
};

export type OptionsMlMetrics = {
  roc_auc?: number | null;
  average_precision?: number | null;
  train_rows?: number | null;
  test_rows?: number | null;
  total_rows?: number | null;
  positive_rate?: number | null;
};

export type OptionsMlDataset = {
  rows?: number | null;
  total_rows?: number | null;
  train_rows?: number | null;
  test_rows?: number | null;
  positive_rate?: number | null;
  date_range?: { min?: string | null; max?: string | null } | null;
  label_counts?: Record<string, number> | null;
  label_reason_counts?: Record<string, number> | null;
  config?: Record<string, any> | null;
  dataset_blob?: string | null;
};

export type OptionsMlCard = {
  model_kind?: string;
  display_name?: string;
  display_status?: string;
  status?: string;
  model_version?: string | null;
  trained_at?: string | null;
  performance_grade?: string | null;
  performance_tone?: string | null;
  positive_class?: string | null;
  use_case?: string | null;
  decision_horizon?: string | null;
  target_definition?: string | null;
  governance_note?: string | null;
  feature_count?: number | null;
  metrics?: OptionsMlMetrics | null;
  dataset?: OptionsMlDataset | null;
  top_features?: OptionsMlFeature[] | null;
};

export type OptionsMlOverview = {
  as_of?: string | null;
  entry?: OptionsMlCard | null;
  exit?: OptionsMlCard | null;
};

type Props = {
  overview?: OptionsMlOverview | null;
  compact?: boolean;
  title?: string;
  subtitle?: string;
};

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

function fmtNumber(value?: number | null, digits = 0): string {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function fmtPct(value?: number | null, digits = 1): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

function toneStyle(tone?: string | null): React.CSSProperties {
  switch (tone) {
    case "success":
      return {
        backgroundColor: "rgba(16, 185, 129, 0.14)",
        border: "1px solid rgba(16, 185, 129, 0.28)",
        color: ui.textPrimary,
      };
    case "warning":
      return {
        backgroundColor: "rgba(245, 158, 11, 0.14)",
        border: "1px solid rgba(245, 158, 11, 0.28)",
        color: ui.textPrimary,
      };
    default:
      return {
        backgroundColor: "rgba(148, 163, 184, 0.14)",
        border: "1px solid rgba(148, 163, 184, 0.24)",
        color: ui.textPrimary,
      };
  }
}

function badgeStyle(tone?: string | null): React.CSSProperties {
  return {
    ...toneStyle(tone),
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
}

function dateRangeLabel(dataset?: OptionsMlDataset | null): string {
  const min = dataset?.date_range?.min;
  const max = dataset?.date_range?.max;
  if (!min && !max) return "—";
  const short = (value?: string | null) => (value ? String(value).slice(0, 10) : "—");
  return `${short(min)} → ${short(max)}`;
}

function featureLabel(feature?: OptionsMlFeature | null): string {
  return feature?.label || feature?.feature || "Feature";
}

const ModelCard: React.FC<{ card?: OptionsMlCard | null; compact?: boolean }> = ({ card, compact }) => {
  if (!card) return null;
  const metrics = card.metrics || {};
  const dataset = card.dataset || {};
  const topFeatures = Array.isArray(card.top_features) ? card.top_features.slice(0, compact ? 4 : 6) : [];

  return (
    <Card style={cardStyle}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>
              {card.model_kind || "model"}
            </div>
            <div className="mt-1 text-lg font-semibold">{card.display_name || "Options model"}</div>
            <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>
              {card.use_case || "Decision support overlay"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span style={badgeStyle(card.performance_tone)}>{card.performance_grade || "monitor"}</span>
            <span style={badgeStyle(null)}>{card.display_status || card.status || "unknown"}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>ROC AUC</div>
            <div className="mt-1 font-semibold">{fmtNumber(metrics.roc_auc, 3)}</div>
          </div>
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Avg Precision</div>
            <div className="mt-1 font-semibold">{fmtNumber(metrics.average_precision, 3)}</div>
          </div>
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Rows</div>
            <div className="mt-1 font-semibold">{fmtNumber(dataset.rows ?? metrics.total_rows, 0)}</div>
          </div>
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Positive Rate</div>
            <div className="mt-1 font-semibold">{fmtPct(dataset.positive_rate ?? metrics.positive_rate, 1)}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Model Version</div>
            <div className="mt-1 text-sm font-medium" style={{ color: ui.textSecondary }}>
              {card.model_version || "Latest blob"}
            </div>
          </div>
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Decision Horizon</div>
            <div className="mt-1 text-sm font-medium" style={{ color: ui.textSecondary }}>
              {card.decision_horizon || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs" style={{ color: ui.textMuted }}>Training Window</div>
            <div className="mt-1 text-sm font-medium" style={{ color: ui.textSecondary }}>
              {dateRangeLabel(dataset)}
            </div>
          </div>
        </div>

        {!compact ? (
          <>
            <div className="mt-4 text-sm" style={{ color: ui.textSecondary }}>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>Target:</span> {card.target_definition || "—"}
            </div>
            <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>Governance:</span> {card.governance_note || "—"}
            </div>
          </>
        ) : null}

        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Top Features</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {topFeatures.length ? (
              topFeatures.map((feature) => (
                <span
                  key={`${card.model_kind}-${feature.feature}`}
                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: "rgba(59, 130, 246, 0.10)",
                    border: "1px solid rgba(59, 130, 246, 0.20)",
                    color: ui.textPrimary,
                  }}
                >
                  {featureLabel(feature)} · {fmtPct(feature.importance_pct, 1)}
                </span>
              ))
            ) : (
              <div className="text-sm" style={{ color: ui.textMuted }}>Feature importance not available.</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const OptionsMlBriefing: React.FC<Props> = ({
  overview,
  compact = false,
  title = "Options ML intelligence",
  subtitle = "Institutional view of current entry and exit model readiness, scale, and feature drivers.",
}) => {
  if (!overview?.entry && !overview?.exit) return null;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>
          {subtitle}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ModelCard card={overview?.entry} compact={compact} />
        <ModelCard card={overview?.exit} compact={compact} />
      </div>
    </div>
  );
};

export default OptionsMlBriefing;
